import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sun, Moon, Loader2, Mail, Shield } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const PARTNER_API = process.env.REACT_APP_PARTNER_BACKEND_URL || 'http://localhost:8081';

const partnerAxios = axios.create({ baseURL: PARTNER_API });

const PartnerLogin = ({ setIsAuthenticated }) => {
  const { theme, toggleTheme } = useTheme();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    company_name: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSendOtp = async () => {
    setLoading(true);
    try {
      await partnerAxios.post('/api/auth/send-otp', { email: formData.email });
      toast.success('OTP sent to your email!');
      setOtpSent(true);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (forgotPassword) {
        if (forgotStep === 1) {
          await partnerAxios.post('/api/auth/forgot-password/send-otp', { email: formData.email });
          toast.success('OTP sent to your email!');
          setForgotStep(2);
          return;
        }
        if (forgotStep === 2) {
          await partnerAxios.post('/api/auth/verify-otp', { email: formData.email, otp });
          toast.success('OTP verified!');
          setForgotStep(3);
          return;
        }
        if (forgotStep === 3) {
          await partnerAxios.post('/api/auth/reset-password', { email: formData.email, otp, new_password: newPassword });
          toast.success('Password reset successfully! Please sign in.');
          setForgotPassword(false);
          setForgotStep(1);
          setOtp('');
          setNewPassword('');
          return;
        }
      }

      if (!isLogin) {
        await partnerAxios.post('/api/auth/verify-otp', { email: formData.email, otp });
      }

      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const response = await partnerAxios.post(endpoint, formData);
      localStorage.setItem('partner_token', response.data.token);
      setIsAuthenticated(true);
      toast.success(isLogin ? 'Login successful!' : 'Registration successful!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    if (forgotPassword) {
      if (forgotStep === 1) return 'Forgot Password';
      if (forgotStep === 2) return 'Verify Email';
      return 'New Password';
    }
    return isLogin ? 'Welcome Back' : 'Get Started';
  };

  const getDescription = () => {
    if (forgotPassword) {
      if (forgotStep === 1) return 'Enter your email to receive a reset code';
      if (forgotStep === 2) return 'Check your inbox for the 6-digit code';
      return 'Enter your new password';
    }
    return isLogin ? 'Sign in to your partner dashboard' : 'Join the OptOps partner program';
  };

  const resetState = () => {
    setOtpSent(false);
    setOtp('');
    setForgotPassword(false);
    setForgotStep(1);
    setNewPassword('');
  };

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 flex relative">
      {/* Subtle dot-grid texture, not gradient blobs */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.04] dark:opacity-[0.06]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
          backgroundSize: '24px 24px',
          color: 'currentColor',
        }}
      />

      {/* Theme toggle */}
      <button
        type="button"
        onClick={toggleTheme}
        className="absolute top-6 right-6 z-30 p-2 rounded-lg text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white transition-colors"
        title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
      >
        {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </button>

      {/* Left rail — partner-specific brand + quiet feature bullets */}
      <div className="hidden lg:flex flex-col justify-between w-[44%] xl:w-[40%] px-12 xl:px-16 py-14 bg-zinc-50 dark:bg-zinc-900/40 border-r border-zinc-200 dark:border-zinc-800 relative z-10">
        <div>
          <div className="flex items-center gap-3">
            <img
              src="/optops-logo.png"
              alt="OptOps"
              className="h-12 w-auto dark:invert dark:hue-rotate-180"
            />
            <div className="flex flex-col">
              <span className="text-zinc-900 dark:text-white font-semibold tracking-tight text-2xl leading-none">
                OptOps
              </span>
              <span className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                Partner portal
              </span>
            </div>
          </div>

          <h1 className="mt-16 text-4xl xl:text-5xl font-semibold tracking-tight text-zinc-900 dark:text-white leading-[1.1]">
            Refer customers,
            <span className="block text-zinc-400 dark:text-zinc-500 font-normal mt-2">
              earn recurring commission.
            </span>
          </h1>

          <p className="mt-6 text-zinc-600 dark:text-zinc-400 text-base leading-relaxed max-w-sm">
            Track referrals, monitor conversion, and see commission accrual
            in real time. Payouts settled monthly.
          </p>

          <ul className="mt-12 space-y-4 text-sm text-zinc-700 dark:text-zinc-300">
            {[
              'Referral tracking with attribution',
              'Commission dashboard + payout history',
              'Real-time conversion notifications',
              'Net-30 payment terms',
            ].map((item) => (
              <li key={item} className="flex items-center gap-3">
                <span className="w-1 h-1 rounded-full bg-zinc-400 dark:bg-zinc-500" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="text-xs text-zinc-500 dark:text-zinc-500">
          <div className="flex items-center gap-4">
            <a href="https://optops.ai" className="hover:text-zinc-900 dark:hover:text-white transition-colors">optops.ai</a>
            <a href="https://optops.ai/privacy-policy" className="hover:text-zinc-900 dark:hover:text-white transition-colors">Privacy</a>
            <a href="https://optops.ai/terms-of-service" className="hover:text-zinc-900 dark:hover:text-white transition-colors">Terms</a>
          </div>
        </div>
      </div>

      {/* Right side — auth form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 relative z-10">
        <div className="w-full max-w-sm">
          {/* Mobile brand (hidden on lg) */}
          <div className="lg:hidden flex items-center justify-center gap-2 mb-10">
            <img
              src="/optops-logo.png"
              alt="OptOps"
              className="h-10 w-auto dark:invert dark:hue-rotate-180"
            />
            <div className="flex flex-col items-start">
              <span className="text-zinc-900 dark:text-white font-semibold text-xl leading-none">OptOps</span>
              <span className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">Partner portal</span>
            </div>
          </div>

          <h2 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-white">
            {getTitle()}
          </h2>
          <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
            {getDescription()}
          </p>

          <div className="mt-8">
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && !forgotPassword && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="full_name" className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Full Name</Label>
                    <Input id="full_name" name="full_name" type="text" placeholder="John Doe" required value={formData.full_name} onChange={handleChange}
                      className="h-10 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:border-zinc-900 dark:focus:border-zinc-300 focus:ring-1 focus:ring-zinc-900 dark:focus:ring-zinc-300" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_name" className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Company Name</Label>
                    <Input id="company_name" name="company_name" type="text" placeholder="Acme Inc." required value={formData.company_name} onChange={handleChange}
                      className="h-10 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:border-zinc-900 dark:focus:border-zinc-300 focus:ring-1 focus:ring-zinc-900 dark:focus:ring-zinc-300" />
                  </div>
                </>
              )}

              {(!forgotPassword || forgotStep === 1) && (
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Email Address</Label>
                  <div className="flex gap-2">
                    <Input
                      id="email" name="email" type="email" placeholder="you@company.com" required
                      value={formData.email}
                      onChange={(e) => { handleChange(e); if (!isLogin) setOtpSent(false); }}
                      className="h-10 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:border-zinc-900 dark:focus:border-zinc-300 focus:ring-1 focus:ring-zinc-900 dark:focus:ring-zinc-300"
                    />
                    {!isLogin && !forgotPassword && (
                      <Button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={loading || !formData.email}
                        className="h-10 px-4 whitespace-nowrap bg-zinc-900 hover:bg-zinc-800 dark:bg-white dark:hover:bg-zinc-200 text-white dark:text-zinc-900 font-medium"
                      >
                        {loading && !otpSent ? <Loader2 className="h-4 w-4 animate-spin" /> : otpSent ? 'Resend' : 'Send OTP'}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {!isLogin && !forgotPassword && (
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Password</Label>
                  <Input id="password" name="password" type="password" placeholder="••••••••" required value={formData.password} onChange={handleChange}
                    className="h-10 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:border-zinc-900 dark:focus:border-zinc-300 focus:ring-1 focus:ring-zinc-900 dark:focus:ring-zinc-300" />
                </div>
              )}

              {!isLogin && otpSent && !forgotPassword && (
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Enter OTP sent to {formData.email}</Label>
                  <Input type="text" placeholder="123456" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value)} required
                    className="h-10 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:border-zinc-900 dark:focus:border-zinc-300 focus:ring-1 focus:ring-zinc-900 dark:focus:ring-zinc-300 text-center text-lg tracking-widest" />
                </div>
              )}

              {forgotPassword && forgotStep === 2 && (
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Enter OTP sent to {formData.email}</Label>
                  <Input type="text" placeholder="123456" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value)} required
                    className="h-10 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:border-zinc-900 dark:focus:border-zinc-300 focus:ring-1 focus:ring-zinc-900 dark:focus:ring-zinc-300 text-center text-lg tracking-widest" />
                </div>
              )}

              {forgotPassword && forgotStep === 3 && (
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-zinc-700 dark:text-zinc-300">New Password</Label>
                  <Input type="password" placeholder="••••••••" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required
                    className="h-10 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:border-zinc-900 dark:focus:border-zinc-300 focus:ring-1 focus:ring-zinc-900 dark:focus:ring-zinc-300" />
                </div>
              )}

              {isLogin && !forgotPassword && (
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Password</Label>
                  <Input id="password" name="password" type="password" placeholder="••••••••" required value={formData.password} onChange={handleChange}
                    className="h-10 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:border-zinc-900 dark:focus:border-zinc-300 focus:ring-1 focus:ring-zinc-900 dark:focus:ring-zinc-300" />
                  <button
                    type="button"
                    onClick={() => { setForgotPassword(true); setForgotStep(1); setOtp(''); setNewPassword(''); }}
                    className="text-xs text-primary hover:text-primary-hover hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-10 bg-zinc-900 hover:bg-zinc-800 dark:bg-white dark:hover:bg-zinc-200 text-white dark:text-zinc-900 font-medium transition-colors mt-6"
                disabled={loading || (!isLogin && !otpSent && !forgotPassword)}
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Processing...</>
                ) : forgotPassword && forgotStep === 1 ? (
                  <><Mail className="mr-2 h-5 w-5" />Send OTP</>
                ) : forgotPassword && forgotStep === 2 ? (
                  <><Shield className="mr-2 h-5 w-5" />Verify OTP</>
                ) : forgotPassword && forgotStep === 3 ? (
                  <><Shield className="mr-2 h-5 w-5" />Reset Password</>
                ) : isLogin ? (
                  <><Shield className="mr-2 h-5 w-5" />Sign In</>
                ) : (
                  <>Sign Up</>
                )}
              </Button>
            </form>

            <div className="mt-8 text-center">
              <button
                type="button"
                onClick={() => { setIsLogin(!isLogin); resetState(); }}
                className="text-sm text-zinc-700 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white font-medium transition-colors"
              >
                {isLogin ? "Don't have an account? Sign up →" : '← Already have an account? Sign in'}
              </button>
              {forgotPassword && (
                <button
                  type="button"
                  onClick={() => { setForgotPassword(false); setForgotStep(1); setOtp(''); setNewPassword(''); }}
                  className="block mx-auto mt-2 text-sm text-zinc-700 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white font-medium transition-colors"
                >
                  ← Back to Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerLogin;
